import useSessionTimeout from "~/services/session/SessionTimeOut";
import { ChangeEvent, useEffect, useState } from "react";
import { PropRow, SortTable } from "~/models/Commons ";
import { LABELS } from "~/resources/Labels";
import AddIcon from "@mui/icons-material/Add";
import { VmComponentsHeader } from "~/models/vmtemplate/ComponentsHeader";
import {
  attachResourcesToComponentes,
  displayEntries,
  sortScripts,
} from "~/Utils/Utils";
import { Entries } from "~/models/enums/Entries";
import { PayloadType } from "~/models/types/Payload";
import { useGetComponentsQuery } from "~/services/vm/Components";
import { AddVmGeneric, VmComponentType } from "./submenu/AddVmGeneric";
import { SIMPLMenuHeader } from "../../components/commons/header/SIMPLMenuHeader";
import { Layout } from "../../components/commons/layout/Layout";
import { LoadingWrapper } from "../../components/commons/spiner/SimpleLoaderWrapper";
import { SIMPLTable } from "../../components/commons/table/SimplTable";
import { SIMPLButtonCustomized } from "../../components/commons/button/SIMPLButtonCustumized";
import { Alerts } from "../messages/Alerts";

interface Props {
  type: VmComponentType;
  addButtonLabel: string;
  addModalTitle: string;
  contentLabel: string;
  contentMessage: string;
}

export const SIMPLVmTable = ({
  type,
  addButtonLabel,
  addModalTitle,
  contentLabel,
  contentMessage,
}: Props) => {
  const [active] = useState(true);
  const [showFilter, setShowFilter] = useState(false);
  const [rows, setRows] = useState<PropRow[]>([]);
  const [size, setSize] = useState(5);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [showPagination, setShowPagination] = useState(true);
  const [totalElements, setTotalElements] = useState(0);
  const [copyModalComponentSuccess, setCopyModalComponentSuccess] =
    useState(false);
  const [copyModalComponentError, setCopyModalComponentError] = useState(false);

  type ResponseDetails = {
    title: string;
    message: string;
  };
  const [responseDetailsComponent, setResponseDetailsComponent] =
    useState<ResponseDetails | null>(null);
  const INACTIVITY_LIMIT = Number(import.meta.env.VITE_INACTIVITY_LIMIT);
  const FORCED_LOGOUT_LIMIT_EXPIRED_TOKEN = Number(
    import.meta.env.VITE_FORCED_LOGOUT_LIMIT_EXPIRED_TOKEN,
  );

  const { data, refetch, isFetching, isLoading, isError } =
    useGetComponentsQuery({ page: 0, size: 999, active, type });

  const [sortConfig, setSortConfig] = useState<SortTable>({
    key: "",
    direction: LABELS.ASC,
  });
  const handleSort = (key: string) => {
    const direction =
      sortConfig.key === key && sortConfig.direction === LABELS.ASC
        ? LABELS.Desc
        : LABELS.ASC;
    setSortConfig({ key, direction });
  };

  useSessionTimeout({
    inactivityLimit: INACTIVITY_LIMIT,
    forcedLogoutLimit: FORCED_LOGOUT_LIMIT_EXPIRED_TOKEN,
    storage: sessionStorage,
  });

  const handlePaginationSize = () => {
    if ([Entries.Ten, Entries.Twenty].includes(size)) setPage(0);
  };

  const initComponents = () => {
    const payload = data?.payload as PayloadType | undefined;
    if (!payload || payload.length === 0) {
      setShowPagination(false);
      return;
    }

    const componentMergedList = attachResourcesToComponentes(payload);
    const sortedScripts = sortScripts(componentMergedList, sortConfig);
    const customizedList = displayEntries(sortedScripts, totalElements, 999);

    setTotal(payload?.page?.totalPages ?? 0);
    if (payload?.page?.totalElements)
      setTotalElements(payload?.page?.totalElements);

    const scriptRows: PropRow[] = customizedList.map((item: any) => ({
      row: item,
    }));
    setRows(scriptRows);
    setShowPagination(true);
  };

  useEffect(() => {
    initComponents();
  }, [data, active, sortConfig, size, total, totalElements]);
  useEffect(() => {
    setPage(0);
    handlePaginationSize();
  }, [active, size]);

  const handleCloseAddModal = () => setIsAddModalOpen(false);
  const showFilterHandler = () => setShowFilter((prev) => !prev);
  const handleSizeChange = (e: ChangeEvent<HTMLSelectElement>) =>
    setSize(Number(e.target.value));
  const handleResetAll = () => {
    setPage(0);
    setShowFilter(false);
  };
  const handlePageChange = (page: number) => setPage(page);

  const renderButtonAddProvider = () => (
    <SIMPLButtonCustomized
      label={addButtonLabel}
      buttonHandlerAction={() => setIsAddModalOpen(true)}
      secondIcon={<AddIcon fontSize="small" />}
      primary
    />
  );

  const renderAddProviderModal = () =>
    isAddModalOpen && (
      <AddVmGeneric
        editedItem={null}
        setConfig={setSortConfig}
        refetch={refetch}
        onCloseModal={handleCloseAddModal}
        title={addModalTitle}
        config={{ componentType: type, contentLabel, contentMessage }}
        dialogProps={{
          onSuccess: (title, message) => {
            setResponseDetailsComponent({ title, message });
            setCopyModalComponentSuccess(true);
          },
          onError: (title, message) => {
            setResponseDetailsComponent({ title, message });
            setCopyModalComponentError(true);
          },
        }}
      />
    );
  const renderComponentsAlertDialog = () => (
    <Alerts
      showSuccess={copyModalComponentSuccess}
      showError={copyModalComponentError}
      title={responseDetailsComponent?.title}
      successMessage={responseDetailsComponent?.message || ""}
      errorMessage={responseDetailsComponent?.message || ""}
      onCloseSucessDialog={() => setCopyModalComponentSuccess(false)}
      onCloseErrorDialog={() => setCopyModalComponentError(false)}
      enabledSuccess={copyModalComponentSuccess}
      enabledError={copyModalComponentError}
    />
  );
  return (
    <SIMPLMenuHeader>
      {renderComponentsAlertDialog()}
      <Layout>
        {renderAddProviderModal()}
        <LoadingWrapper
          loadingTitle={`Loading ${addModalTitle}...`}
          isLoading={isLoading || isFetching}
          isError={isError}
        >
          <SIMPLTable
            title={`${addModalTitle} Management`}
            clear={handleResetAll}
            button={renderButtonAddProvider()}
            page={page}
            size={size}
            showPagination={showPagination}
            active={true}
            showHandler={showFilterHandler}
            showButtonMoreActions={false}
            filterActive={showFilter}
            tableHeaders={VmComponentsHeader}
            rows={rows}
            onChangePage={handlePageChange}
            onChange={handleSizeChange}
            sortConfig={sortConfig}
            handleSort={handleSort}
            chipLabels={[]}
            lastBorder="border-l border-white"
          />
        </LoadingWrapper>
      </Layout>
    </SIMPLMenuHeader>
  );
};
